import GLTileLayer from '@planet/maps/layer/WebGLTile';
import GeoJSON from 'ol/format/GeoJSON';
import GroupLayer from '@planet/maps/layer/Group';
import React, {useEffect, useMemo, useRef, useState} from 'react';
import TileLayer from '@planet/maps/layer/Tile';
import XYZSource from '@planet/maps/source/XYZ';
import {KEY_SPACE} from 'keycode-js';
import {array, bool, func, number, object, string} from 'prop-types';
import {getVectorContext} from 'ol/render';

import {CLIP_STYLE} from '@explorer/util/map-edit-styles';
import {IMG_FILTERS} from '@explorer/constants';
import {Statistics} from '@explorer/util/stats';
import {createNumpySource} from './numpy-source.js';
import {createNumpyStyle} from '@explorer/util/map';
import {trackEvent} from '@explorer/analytics';

const RasterTilesLayer = ({
  olView,
  isAnalytic = false,
  pixelDepth = 255,
  numpyUrl = '',
  enhancementOn = false,
  rasterConfigs = [],
  clipGeometry = null,
  visible = true,
  enhanceFilters = {},
  onTileLoad,
}) => {
  const numpyLayer = useRef(null);

  const [topLayerVisible, setTopLayerVisible] = useState(true);
  const lastResultId = useMemo(
    () => rasterConfigs[rasterConfigs.length - 1]?.resultId,
    [rasterConfigs]
  );

  // useMemo is used instead of useCallback
  //  so that geometry can be used as a closure in the function
  //  that is returned. The object is returned for some
  //  future-proofing in case we need a onPrerender or
  //  the geometry to be broken out.
  const {onPostrender} = useMemo(() => {
    const geometry = clipGeometry
      ? new GeoJSON().readGeometry(clipGeometry)
      : null;

    return {
      onPostrender: event => {
        if (!geometry) {
          return false;
        }
        const vectorContext = getVectorContext(event);
        event.context.globalCompositeOperation = 'destination-in';
        vectorContext.setStyle(CLIP_STYLE);
        vectorContext.drawGeometry(geometry);
        event.context.globalCompositeOperation = 'source-over';
      },
    };
  }, [clipGeometry]);

  const numpySource = useMemo(() => {
    const mapStats = new Statistics();

    let src = null;
    if (enhancementOn) {
      src = createNumpySource(olView, mapStats, numpyUrl);
      src.on('alltilesloaded', () => {
        let bandSamples = [[0, 0]];

        bandSamples = mapStats.standardDeviation(2);

        let [bMin, bMax] = [bandSamples[0][0], bandSamples[0][1]];
        for (let i = 1; i < 3; i++) {
          bMin = Math.min(bandSamples[i][0], bMin);
          bMax = Math.max(bandSamples[i][1], bMax);
        }

        if (numpyLayer.current) {
          numpyLayer.current.updateStyleVariables({
            bandOrder: isAnalytic ? 'bgrna' : 'rgba',
            bMin,
            bMax,
          });
        }
      });
    }

    return src;
  }, [isAnalytic, enhancementOn, numpyUrl, olView]);

  const numpyStyle = useMemo(() => createNumpyStyle(pixelDepth), [pixelDepth]);

  useEffect(() => {
    // TODO: Maybe write a selector to make this less onerous?
    const filters = enhanceFilters;
    const brightness = filters[IMG_FILTERS.BRIGHTNESS] / 100;
    const contrast = filters[IMG_FILTERS.CONTRAST] / 100;
    const saturation = filters[IMG_FILTERS.SATURATION] / 100;
    if (enhancementOn && numpyLayer.current) {
      numpyLayer.current.updateStyleVariables({
        bx: brightness,
        cx: contrast,
        sx: saturation,
      });
    }
  }, [numpyStyle, enhanceFilters, enhancementOn]);

  const sourceOptions = useMemo(() => {
    const options = {
      crossOrigin: 'anonymous',
    };

    // this prevents needing to use the fetch loader
    //   when `onTileLoad` is undefined.
    if (onTileLoad) {
      options.tileLoadFunction = (imageTile, src) => {
        return fetch(src)
          .then(res => {
            onTileLoad(res);
            return res.blob();
          })
          .then(blob => {
            const reader = new FileReader();
            reader.onload = evt => {
              imageTile.getImage().src = evt.target.result;
            };
            reader.readAsDataURL(blob);
          })
          .catch(() => {
            // do nothing
          });
      };
    }

    return options;
  }, [onTileLoad]);

  const updateVisibility = (event, visible) => {
    const tagName = event.target.tagName.toUpperCase();
    const isInputArea = tagName === 'INPUT' || tagName === 'TEXTAREA';

    if (event.keyCode === KEY_SPACE && !isInputArea) {
      event.preventDefault();
      event.stopPropagation();

      setTopLayerVisible(visible);

      trackEvent('Used space bar to toggle layer visibility');
    }
  };

  useEffect(() => {
    const onKeyDown = event => {
      updateVisibility(event, false);
    };

    const onKeyUp = event => {
      updateVisibility(event, true);
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, []);

  return (
    <GroupLayer visible={visible}>
      {enhancementOn && (
        <GLTileLayer
          ref={numpyLayer}
          key={numpyUrl}
          source={numpySource}
          style={numpyStyle}
        />
      )}
      {!enhancementOn &&
        rasterConfigs.map(config => {
          return (
            <TileLayer
              key={`${config.id}_${config.maxZoom}`}
              onPostrender={onPostrender}
              options={{
                extent: config.extent,
              }}
              visible={
                config.resultId === lastResultId ? topLayerVisible : true
              }
            >
              <XYZSource
                url={config.url}
                options={{
                  maxZoom: Math.ceil(config.maxZoom),
                  ...sourceOptions,
                }}
              />
            </TileLayer>
          );
        })}
    </GroupLayer>
  );
};

RasterTilesLayer.propTypes = {
  rasterConfigs: array,
  clipGeometry: object,
  numpyUrl: string,
  enhancementOn: bool,
  pixelDepth: number,
  isAnalytic: bool,
  enhanceFilters: object,
  visible: bool,
  olView: object,
  onTileLoad: func,
};

export default RasterTilesLayer;
